import React, { useEffect } from "react";
import { Formik, Form, FormikHelpers } from "formik";
import * as Yup from "yup";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../Appstore/store";
import { UpdateSmtpgateways } from "../Features/Smtpgatewaysdata";
import { GetSmtpGatewayView } from "../Features/Smtpgatewaysviewdata";
import { IoMdArrowRoundBack } from "react-icons/io";
import { setIsLoading } from "../Features/loadingSlice";
import { toast } from "sonner";

interface FormValues {
  id?: number;
  gateway_name?: string;
  client_id?: string;
  client_secret?: string;
  refresh_token?: string;
  email?: string;
  smtp_gateway?: number;
  from_email?: string;
  tenant_id: string;
}

const Updatesmsgateways: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const { id }: any = useParams();

  const { smtpgatewaysviewdata } = useSelector(
    (state: RootState) => state.smtpgatewaysview
  );
  const record: FormValues | any = smtpgatewaysviewdata.find(
    (recodID) => recodID.id === Number(id)
  );

  const validationSchema = Yup.object({
    gateway_name: Yup.string().trim().max(255).required().label("Gateway name"),
    client_id: Yup.string().trim().max(255).required().label("Client Id"),
    client_secret: Yup.string()
      .trim()
      .max(255)
      .required()
      .label("Client secret"),
    refresh_token: Yup.string().trim().required().label("Refresh token"),
    tenant_id: Yup.string()
      .when("smtp_gateway", {
        is: 2,
        then: (schema) => schema.required(),
      })
      .label("Tenant id"),
    email: Yup.string().email().trim().max(255).required().label("Email"),
    smtp_gateway: Yup.number().min(1).max(2).required().label("SMTP Gateway"),
    from_email: Yup.string()
      .email()
      .trim()
      .max(255)
      .required()
      .label("From Email"),
  });

  useEffect(() => {
    if (id) {
      dispatch(setIsLoading(true));
      dispatch(GetSmtpGatewayView(id))
        .unwrap()
        .catch((err) => {
          console.error("Error fetching agent monitor:", err);
        })
        .finally(() => {
          setTimeout(() => dispatch(setIsLoading(false)), 500);
        });
    }
  }, [id, dispatch]);

  const initialValues: FormValues = {
    gateway_name: record?.gateway_name || "",
    client_id: record?.client_id || "",
    client_secret: record?.client_secret || "",
    refresh_token: record?.refresh_token || "",
    email: record?.email || "",
    smtp_gateway: record?.smtp_gateway || 1,
    from_email: record?.from_email || "",
    tenant_id: record?.tenant_id || "",
  };

  const navigatetobackpage = () => {
    navigate(-1);
  };

  return (
    <>
      <div className="wrapper">
        <div className="content-wrapper">
          <section className="content">
            <div className="container-fluid">
              <div className="card mt-4">
                <div className="card-header bg-white p-3 d-flex justify-content-md-between">
                  <h2 className="w-100 mb-md-0 fs-3">Update SMTP Gateway</h2>
                  <Link to={""} className="text-decoration-none">
                    <button
                      onClick={navigatetobackpage}
                      className="btn btn-info text-white d-flex align-items-center fw-semibold"
                    >
                      <IoMdArrowRoundBack /> Back
                    </button>
                  </Link>
                </div>
                <div className="card-body">
                  <Formik
                    initialValues={initialValues}
                    enableReinitialize={true}
                    validationSchema={validationSchema}
                    onSubmit={async (
                      values: FormValues,
                      { setSubmitting }: FormikHelpers<FormValues>
                    ) => {
                      const { id }: any = record;
                      const smsGatewaysData: FormValues = values;
                      try {
                        const response: any = await dispatch(
                          UpdateSmtpgateways({
                            id: id ?? 0,
                            updatedData: smsGatewaysData,
                          })
                        );
                        if (
                          response.payload &&
                          response.payload.status === "success"
                        ) {
                          toast.success(response.payload.message);
                          navigate("/admin/smtp-gateways");
                        } else {
                          toast.error(response.payload.message);
                        }
                      } finally {
                        setSubmitting(false);
                        dispatch(setIsLoading(false));
                      }
                    }}
                  >
                    {({
                      values,
                      errors,
                      touched,
                      handleChange,
                      handleBlur,
                      isSubmitting,
                    }) => (
                      <Form>
                        <div className="row">
                          <div className="form-group mb-3 col-md-6">
                            <label htmlFor="gateway_name">
                              Gateway Name
                              <span className="text-danger">*</span>
                            </label>
                            <input
                              type="text"
                              className={`form-control shadow-none ${
                                touched.gateway_name && errors.gateway_name
                                  ? "is-invalid"
                                  : ""
                              }`}
                              id="gateway_name"
                              name="gateway_name"
                              placeholder="Enter Gateway Name"
                              value={values.gateway_name}
                              onChange={handleChange}
                              onBlur={handleBlur}
                            />
                            {touched.gateway_name && errors.gateway_name && (
                              <div className="text-danger">
                                {errors.gateway_name}
                              </div>
                            )}
                          </div>

                          <div className="form-group mb-3 col-md-6">
                            <label htmlFor="smtp_gateway">
                              SMTP Gateway
                              <span className="text-danger">*</span>
                            </label>
                            <select
                              className={`form-control shadow-none ${
                                touched.smtp_gateway && errors.smtp_gateway
                                  ? "is-invalid"
                                  : ""
                              }`}
                              id="smtp_gateway"
                              name="smtp_gateway"
                              value={values.smtp_gateway}
                              onChange={handleChange}
                              onBlur={handleBlur}
                            >
                              <option value={1}>Google</option>
                              <option value={2}>Microsoft</option>
                            </select>
                            {touched.smtp_gateway && errors.smtp_gateway && (
                              <div className="text-danger">
                                {errors.smtp_gateway}
                              </div>
                            )}
                          </div>
                        </div>

                        <div className="row">
                          <div className="form-group mb-3 col-md-6">
                            <label htmlFor="client_secret">
                              Client Secret
                              <span className="text-danger">*</span>
                            </label>
                            <input
                              type="text"
                              className={`form-control shadow-none ${
                                touched.client_secret && errors.client_secret
                                  ? "is-invalid"
                                  : ""
                              }`}
                              id="client_secret"
                              name="client_secret"
                              placeholder="Enter Client Secret"
                              value={values.client_secret}
                              onChange={handleChange}
                              onBlur={handleBlur}
                            />
                            {touched.client_secret && errors.client_secret && (
                              <div className="text-danger">
                                {errors.client_secret}
                              </div>
                            )}
                          </div>
                          <div className="form-group mb-3 col-md-6">
                            <label htmlFor="refresh_token">
                              Refresh Token
                              <span className="text-danger">*</span>
                            </label>
                            <input
                              type="text"
                              className={`form-control shadow-none ${
                                touched.refresh_token && errors.refresh_token
                                  ? "is-invalid"
                                  : ""
                              }`}
                              id="refresh_token"
                              name="refresh_token"
                              placeholder="Enter Refresh Token"
                              value={values.refresh_token}
                              onChange={handleChange}
                              onBlur={handleBlur}
                            />
                            {touched.refresh_token && errors.refresh_token && (
                              <div className="text-danger">
                                {errors.refresh_token}
                              </div>
                            )}
                          </div>
                        </div>

                        <div className="row">
                          <div className="form-group mb-3 col-md-6">
                            <label htmlFor="client_id">
                              Client ID<span className="text-danger">*</span>
                            </label>
                            <input
                              type="text"
                              className={`form-control shadow-none ${
                                touched.client_id && errors.client_id
                                  ? "is-invalid"
                                  : ""
                              }`}
                              id="client_id"
                              name="client_id"
                              placeholder="Enter Client ID"
                              value={values.client_id}
                              onChange={handleChange}
                              onBlur={handleBlur}
                            />
                            {touched.client_id && errors.client_id && (
                              <div className="text-danger">
                                {errors.client_id}
                              </div>
                            )}
                          </div>

                          <div className="form-group mb-3 col-md-6">
                            <label htmlFor="tenant_id">
                              Tenant Id
                              <span className="text-danger">*</span>
                            </label>
                            <input
                              type="text"
                              className={`form-control shadow-none ${
                                touched.tenant_id && errors.tenant_id
                                  ? "is-invalid"
                                  : ""
                              }`}
                              id="tenant_id"
                              name="tenant_id"
                              placeholder="Enter a From Email"
                              value={values.tenant_id}
                              onChange={handleChange}
                              onBlur={handleBlur}
                            />
                            {touched.tenant_id && errors.tenant_id && (
                              <div className="text-danger">
                                {errors.tenant_id}
                              </div>
                            )}
                          </div>
                        </div>

                        <div className="row">
                          <div className="form-group mb-3 col-md-6">
                            <label htmlFor="email">
                              Email<small>(Authorized)</small>
                              <span className="text-danger">*</span>
                            </label>
                            <input
                              type="text"
                              className={`form-control shadow-none ${
                                touched.email && errors.email
                                  ? "is-invalid"
                                  : ""
                              }`}
                              id="email"
                              name="email"
                              placeholder="Enter Email"
                              value={values.email}
                              onChange={handleChange}
                              onBlur={handleBlur}
                            />
                            {touched.email && errors.email && (
                              <div className="text-danger">{errors.email}</div>
                            )}
                          </div>

                          <div className="form-group mb-3 col-md-6">
                            <label htmlFor="from_email">
                              SMTP From Email
                              <span className="text-danger">*</span>
                            </label>
                            <input
                              type="text"
                              className={`form-control shadow-none ${
                                touched.from_email && errors.from_email
                                  ? "is-invalid"
                                  : ""
                              }`}
                              id="from_email"
                              name="from_email"
                              placeholder="Enter a From Email"
                              value={values.from_email}
                              onChange={handleChange}
                              onBlur={handleBlur}
                            />
                            {touched.from_email && errors.from_email && (
                              <div className="text-danger">
                                {errors.from_email}
                              </div>
                            )}
                          </div>
                        </div>

                        <div className="d-flex">
                          <button
                            type="submit"
                            className="btn btn-primary me-2"
                            disabled={isSubmitting}
                          >
                            {isSubmitting ? (
                              <>
                                <span
                                  className="spinner-border spinner-border-sm"
                                  role="status"
                                  aria-hidden="true"
                                ></span>
                                <span className="ps-2">Save...</span>
                              </>
                            ) : (
                              "Save"
                            )}
                          </button>
                          <Link
                            to="/admin/smtp-gateways"
                            className={`nav-link`}
                          >
                            <button type="button" className="btn btn-secondary">
                              Cancel
                            </button>
                          </Link>
                        </div>
                      </Form>
                    )}
                  </Formik>
                </div>
              </div>
            </div>
          </section>
        </div>
      </div>
    </>
  );
};

export default Updatesmsgateways;
